import { useState, useEffect, useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { format } from "date-fns";
import {
  Receipt,
  ArrowClockwise,
  Warning,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "components/ui/table";
import { Button } from "components/ui/button";
import { Skeleton } from "components/ui/skeleton";
import { formatCurrency } from "shared/utils/currency";

/**
 * A renovation-ledger invoice line that has NO kostenplaats assigned yet.
 * These are global (no building link). They are shown read-only: a kostenplaats
 * must first be added in the boekhouding (Odoo), after which the invoice appears
 * in the normal building-scoped "te importeren" flow. They cannot be imported
 * directly from YallyHub.
 */
export interface UnassignedInvoice {
  partner_id: [number, string] | false;
  date: string;
  name: string;
  move_line_id: [number, string];
  /** Negatief = kost, positief = creditnota. */
  amount: number;
  amount_untaxed: number;
  general_account_id: [number, string];
  account_id: null;
  move_name: string;
  /** Creditnota (RLF): wordt negatief en in het rood getoond. */
  is_credit_note?: boolean;
  /** Link naar de boeking in Odoo — daar wordt de kostenplaats gezet. */
  odoo_url?: string | null;
}

/**
 * Het bedrag van één regel, met het juiste teken: een kost telt op, een
 * creditnota telt af. De backend levert het bedrag in "negatief = kost", dus
 * hier één keer omdraaien.
 */
const regelBedrag = (invoice: UnassignedInvoice): number =>
  -Number(invoice.amount_untaxed ?? invoice.amount ?? 0);

/**
 * "Zonder kostenplaats" — a read-only section in the "Te importeren facturen"
 * tab listing renovation invoices booked on a renovation ledger account that do
 * not have a kostenplaats yet. The user must add the kostenplaats in the
 * boekhouding first; only then can the invoice be imported (via the normal flow).
 */
interface Props {
  /** Bump to make this list refetch its (cached) data (import / global refresh). */
  refreshToken?: number;
  /** True while a global analytics rebuild is running (drives the spinner). */
  isRefreshing?: boolean;
  /**
   * Global "Vernieuwen" handler owned by the host page. When provided the button
   * rebuilds the whole analytics cache (all import sections + KPIs), not just
   * this list.
   */
  onRefresh?: () => void;
}

export const UnassignedInvoices = ({
  refreshToken,
  isRefreshing,
  onRefresh,
}: Props = {}) => {
  const { session } = useSession();

  const [invoices, setInvoices] = useState<UnassignedInvoice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // Spinner reflects either a host-driven global refresh or a local fetch.
  const refreshing = isRefreshing ?? false;

  const fetchInvoices = useCallback(async () => {
    if (!session) return;

    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/available-invoices/unassigned`,
        { method: "GET" },
        session,
      );

      if (!response.ok) {
        const errorText = await response
          .text()
          .catch(() => response.statusText);
        throw new Error(
          `Fout bij ophalen van facturen zonder kostenplaats (${response.status}): ${errorText}`,
        );
      }

      const json = await response.json();
      setInvoices(json.data ?? []);
      setLastUpdated(json.lastUpdated ?? null);
    } catch (error) {
      // Log so Sentry captures the breadcrumb.
      console.error("[UnassignedInvoices] Error fetching invoices:", error);
      toast.error("Fout bij ophalen van facturen zonder kostenplaats");
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Refetch when the host bumps the refresh token (import / global "Vernieuwen").
  useEffect(() => {
    if (refreshToken) fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const totaal = invoices.reduce((som, invoice) => som + regelBedrag(invoice), 0);
  const aantalCreditnotas = invoices.filter(
    (invoice) => invoice.is_credit_note || regelBedrag(invoice) < 0,
  ).length;

  return (
    <div className="px-6 pt-2 pb-6">
      <div className="rounded-lg border border-amber-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <Warning size={20} weight="duotone" className="text-amber-600" />
            <div>
              <h3 className="text-sm font-semibold text-neutral-primary">
                Facturen zonder kostenplaats
              </h3>
              <p className="text-xs text-neutral-secondary-mid">
                Geboekt op een renovatierekening maar nog niet toegewezen aan
                een kostenplaats. Voeg eerst een kostenplaats toe in de
                boekhouding — daarna verschijnen ze vanzelf bij het juiste
                gebouw om te importeren.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-[11px] text-neutral-secondary-mid">
                Laatst bijgewerkt {lastUpdated}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => (onRefresh ? onRefresh() : fetchInvoices())}
              disabled={refreshing || isLoading}
            >
              <ArrowClockwise
                size={14}
                className={refreshing || isLoading ? "animate-spin" : ""}
              />
              Vernieuwen
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-2 p-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-2/3" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <Receipt size={32} weight="duotone" className="text-neutral-300" />
            <p className="text-sm text-neutral-secondary-mid">
              Geen facturen zonder kostenplaats gevonden.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Factuurnummer</TableHead>
                <TableHead>Leverancier</TableHead>
                <TableHead>Grootboekrekening</TableHead>
                <TableHead className="text-right">Bedrag (excl. btw)</TableHead>
                <TableHead className="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => {
                const moveLineId = invoice.move_line_id?.[0];
                const supplierName = Array.isArray(invoice.partner_id)
                  ? invoice.partner_id[1]
                  : "Onbekend";
                const ledger = Array.isArray(invoice.general_account_id)
                  ? invoice.general_account_id[1]
                  : "—";
                const bedrag = regelBedrag(invoice);
                return (
                  <TableRow key={moveLineId}>
                    <TableCell className="whitespace-nowrap">
                      {invoice.date
                        ? format(new Date(invoice.date), "dd/MM/yyyy")
                        : "—"}
                    </TableCell>
                    <TableCell className="font-medium text-neutral-primary">
                      {invoice.move_name || "Onbekend"}
                    </TableCell>
                    <TableCell>{supplierName}</TableCell>
                    <TableCell className="text-neutral-secondary-mid">
                      {ledger}
                    </TableCell>
                    {/* Een creditnota (RLF) verlaagt de kost: minteken en rood,
                        zodat de rij niet als uitgave leest en het totaal
                        hieronder klopt. */}
                    <TableCell
                      className={`text-right tabular-nums whitespace-nowrap ${
                        bedrag < 0 ? "font-medium text-brick-primary" : ""
                      }`}
                    >
                      {formatCurrency(bedrag)}
                    </TableCell>
                    <TableCell className="text-right">
                      {/* Was een chipje dat eruitzag als een knop maar niets
                          deed. De kostenplaats wordt in de boekhouding gezet,
                          dus gaat de link rechtstreeks naar die boeking. */}
                      {invoice.odoo_url ? (
                        <a
                          href={invoice.odoo_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 transition-colors hover:bg-amber-100"
                          title="Open deze boeking in Odoo om er een kostenplaats aan toe te voegen"
                        >
                          Kostenplaats toevoegen in boekhouding
                          <ArrowSquareOut size={11} weight="bold" />
                        </a>
                      ) : (
                        <span className="inline-flex items-center whitespace-nowrap rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                          Kostenplaats toevoegen in boekhouding
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* De optelsom hoort onder de kolom te staan waar ze bij hoort.
                  Ze stond er helemaal niet, en met creditnota's die als uitgave
                  meetelden zou ze ook niet geklopt hebben. */}
              <TableRow className="border-t-2 bg-neutral-50 font-semibold hover:bg-neutral-50">
                <TableCell colSpan={4}>
                  Totaal ({invoices.length} factu
                  {invoices.length === 1 ? "ur" : "ren"}
                  {aantalCreditnotas > 0 &&
                    `, waarvan ${aantalCreditnotas} creditnota${
                      aantalCreditnotas === 1 ? "" : "'s"
                    }`}
                  )
                </TableCell>
                <TableCell
                  className={`text-right tabular-nums whitespace-nowrap ${
                    totaal < 0 ? "text-brick-primary" : ""
                  }`}
                >
                  {formatCurrency(totaal)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default UnassignedInvoices;
